import { useEffect, useState } from "react";

import type { EchoflicksSessionMedia, EchoflicksVideoSession } from "../types";
import { resolveMediaSource } from "./mediaSource";
import { sessionScriptSource, useMediaUrl } from "./useMediaUrl";

/**
 * Loads and parses the director-script JSON for a session from its private signed URL.
 *
 * This mirrors the loader inside ScriptJsonViewer so the Scene Breakdown view can read the
 * same envelope (including the orchestrator's `diagnostics.scene_breakdown`) without coupling
 * the two components. Kept intentionally generic — the envelope is returned untyped and each
 * consumer narrows the fields it needs.
 */
export type ScriptEnvelopeLike = Record<string, unknown>;

export type ScriptEnvelopeState =
  | { status: "idle" | "loading" }
  | { status: "loaded"; envelope: ScriptEnvelopeLike }
  | { status: "error"; message: string };

export function useScriptEnvelope(
  session: EchoflicksVideoSession | null | undefined,
  media: EchoflicksSessionMedia[],
) {
  const scriptMedia = media.find((item) => item.kind === "script_json");
  const scriptUrlState = useMediaUrl(
    resolveMediaSource(
      session ? sessionScriptSource(session) : null,
      scriptMedia,
    ),
  );
  const scriptUrl = scriptUrlState.url;
  const [state, setState] = useState<ScriptEnvelopeState>({
    status: scriptUrl ? "loading" : "idle",
  });

  useEffect(() => {
    if (!scriptUrl) {
      setState({ status: "idle" });
      return;
    }

    let active = true;
    setState({ status: "loading" });
    fetch(scriptUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Script request failed with ${response.status}`);
        }
        return (await response.json()) as ScriptEnvelopeLike;
      })
      .then((envelope) => {
        if (active) setState({ status: "loaded", envelope });
      })
      .catch((error) => {
        if (active) {
          setState({
            status: "error",
            message:
              error instanceof Error ? error.message : "Could not load script",
          });
        }
      });

    return () => {
      active = false;
    };
  }, [scriptUrl]);

  return { state, scriptUrl, scriptUrlState };
}
